// Shared format utilities — date/time parsing and display
import { FALLBACK_TZ } from '../../config/env';
import { PRIORITIES } from '../constants/priorities';

function detectTimeZone() {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detected === 'Asia/Saigon'
      ? 'Asia/Ho_Chi_Minh'
      : detected || FALLBACK_TZ;
  } catch {
    return FALLBACK_TZ;
  }
}

export const DISPLAY_TZ = detectTimeZone();

// The backend sends naive UTC timestamps with no timezone designator.
// Append "Z" so they are parsed as UTC before display or comparison.
export function parseApiDate(value) {
  if (value == null || value instanceof Date) return value;
  if (typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(value + 'Z');
  }
  return new Date(value);
}

export function parseValidApiDate(value) {
  const date = parseApiDate(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

const dateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_TZ,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const monthDayFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_TZ,
  month: 'short',
  day: 'numeric',
});
const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const weekdayFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: DISPLAY_TZ,
  weekday: 'long',
});
const japaneseDateTimeFmt = new Intl.DateTimeFormat('ja-JP-u-ca-gregory', {
  timeZone: DISPLAY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const INVALID_DATE_LABEL = '—';

function getDateTimePart(parts, type) {
  return parts.find(part => part.type === type)?.value || '';
}

export const fmtDate = value => {
  const date = parseValidApiDate(value);
  return date ? dateFmt.format(date) : INVALID_DATE_LABEL;
};

export const fmtDateTime = value => {
  const date = parseValidApiDate(value);
  return date
    ? `${monthDayFmt.format(date)} • ${timeFmt.format(date)}`
    : INVALID_DATE_LABEL;
};

export const fmtWeekday = value => {
  const date = parseValidApiDate(value);
  return date ? weekdayFmt.format(date) : INVALID_DATE_LABEL;
};

export const fmtJapaneseDateTime = value => {
  const date = parseValidApiDate(value);
  if (!date) return INVALID_DATE_LABEL;

  const parts = japaneseDateTimeFmt.formatToParts(date);
  const year = getDateTimePart(parts, 'year');
  const month = getDateTimePart(parts, 'month');
  const day = getDateTimePart(parts, 'day');
  const hour = getDateTimePart(parts, 'hour');
  const minute = getDateTimePart(parts, 'minute');
  return `${year}/${month}/${day} ${hour}:${minute}`;
};

export const fmtJapaneseDateTimeRange = (from, to) => (
  `${fmtJapaneseDateTime(from)} – ${fmtJapaneseDateTime(to)}`
);

export function getDisplayTimeZoneLabel() {
  if (DISPLAY_TZ === 'Asia/Ho_Chi_Minh') return 'Vietnam (UTC+07:00)';
  if (DISPLAY_TZ === 'Asia/Tokyo') return 'Japan (UTC+09:00)';
  return DISPLAY_TZ;
}

export function timeAgo(value) {
  const date = parseValidApiDate(value);
  if (!date) return INVALID_DATE_LABEL;

  const ms = Date.now() - date.getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes + 'm ago';
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h ago';
  const days = Math.floor(hours / 24);
  if (days < 7) return days + 'd ago';
  return fmtDate(date);
}

export function slaTimeLeft(request) {
  if (
    request.status === 'resolved' ||
    request.status === 'closed' ||
    !request.sla
  ) {
    return null;
  }

  const slaDate = parseValidApiDate(request.sla);
  if (!slaDate) return null;

  const ms = slaDate.getTime() - Date.now();
  if (ms < 0) {
    const hours = Math.ceil(-ms / 3600000);
    return {
      overdue: true,
      label: 'Overdue ' + (hours >= 24 ? Math.floor(hours / 24) + 'd' : hours + 'h'),
    };
  }
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) {
    return { warn: true, label: Math.ceil(ms / 60000) + 'm left' };
  }
  if (hours < 24) return { warn: hours < 4, label: hours + 'h left' };
  return { label: Math.floor(hours / 24) + 'd left' };
}

export function priorityRank(priority) {
  return (PRIORITIES[priority] || {}).rank || 0;
}
